import JsPDF from "jspdf";
import axios from "axios";

// Helper function to resize and compress image
async function resizeImage(
  base64Image,
  maxWidth = 200,
  maxHeight = 200,
  quality = 0.7,
  imageName = "image"
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to base64 with compression
      const resizedBase64 = canvas.toDataURL("image/jpeg", quality);

      resolve(resizedBase64);
    };
    img.onerror = (error) => {
      reject(error);
    };
    img.src = base64Image;
  });
}

/**
 * Generates a PDF with login information for a user
 * @param {Object} params - The parameters object
 * @param {string} params.userName - The username to display
 * @param {string} params.password - The password to display (optional)
 * @param {boolean} params.withPwd - Whether to include the password in the PDF
 * @param {Object} params.settings - Settings object containing image and activeBuilding
 * @param {string} params.qrDataImage - Base64 QR code image data
 * @param {string} params.userType - User type: 'parent', 'student', or 'teacher' (optional)
 */

export const generateLoginPdf = async ({
  userName,
  password = "",
  withPwd = false,
  settings,
  qrDataImage,
  userType = null,
  t,
  locale = "fr-FR",
}) => {
  // Validate required parameters
  if (!userName) {
    throw new Error("userName is required");
  }
  if (!settings) {
    throw new Error("settings is required");
  }
  if (!qrDataImage) {
    throw new Error("qrDataImage is required");
  }

  // Find the main logo image based on active building
  let schoolLogo = null;
  let arrayOfImages = settings.image;
  for (var i = 0; i < arrayOfImages.length; i++) {
    if (settings.activeBuilding === arrayOfImages[i].db) {
      var imgSrc = arrayOfImages[i].logo;
      schoolLogo = arrayOfImages[i].logo;
    }
  }

  let imgData = "data:image/jpeg;base64, ";

  await axios
    .get(imgSrc, {
      responseType: "arraybuffer",
    })
    .then((response) => {
      imgData += Buffer.from(response.data, "binary").toString("base64");
    });

  try {
    imgData = await resizeImage(imgData, 200, 200, 0.7, "Building Logo");
  } catch (error) {
    // Keep original if resize fails
  }

  // Create PDF document
  const doc = new JsPDF();
  doc.setFont("Amiri");
  const isArabic = locale === "ar-AR";

  // Get current date with locale-specific formatting
  const date = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  // Generate title with user type if provided
  let finalTitle = t("pdfLogin.loginInfo");
  if (userType) {
    const userTypeLabels = {
      parent: "EdParent",
      student: "EdStudent",
      teacher: "EdTeacher",
    };
    const userTypeLabel = userTypeLabels[userType.toLowerCase()] || userType;
    finalTitle = `${finalTitle} - ${userTypeLabel}`;
  }

  // Add title
  doc.setFontSize(15);
  doc.setFont("Amiri", "Bold");
  const pageWidth = doc.internal.pageSize.getWidth();
  const titleWidth = doc.getTextWidth(finalTitle);
  const titleX = (pageWidth - titleWidth) / 2;
  doc.text(finalTitle, titleX, 18);

  // Add building logo
  doc.setFontSize(10);
  doc.setFont("Amiri", "normal");
  if (imgData) {
    const logoX = isArabic ? pageWidth - 28 : 8; // Right for Arabic, left for others
    doc.addImage(imgData, "JPEG", logoX, 8, 20, 20);
  }

  // Add date
  doc.setFontSize(8);
  const dateX = isArabic ? 8 : pageWidth - 8; // Left for Arabic, right for others
  doc.text(date.toLocaleDateString(locale, options), dateX, 15, {
    align: isArabic ? "left" : "right",
  });

  // Section 1: Login Credentials
  doc.setFontSize(14);
  doc.text(t("pdfLogin.option1"), 15, 40);

  doc.setFontSize(12);
  doc.text(t("pdfLogin.username"), 15, 50);
  doc.setFontSize(11);
  doc.text(userName, 70, 50);

  // Calculate Y position for Section 2 based on whether password is shown
  let section2Y = 75; // Default position when password is shown

  if (withPwd && password) {
    doc.setFontSize(12);
    doc.text(t("pdfLogin.temporaryPassword"), 15, 58);
    doc.setFontSize(11);
    doc.text(password, 70, 58);
  } else {
    // If no password, move Section 2 closer to Section 1
    section2Y = 65;
  }

  // Section 2: QR Code Login
  doc.setFontSize(14);
  doc.text(t("pdfLogin.option2"), 15, section2Y);

  doc.setFontSize(12);
  doc.text(t("pdfLogin.scanQRCode"), 15, section2Y + 8);

  // Add QR code image
  const qrCodeSize = 60;
  const qrCodeX = (pageWidth - qrCodeSize) / 2;
  doc.addImage(
    qrDataImage,
    "JPEG",
    qrCodeX,
    section2Y + 15,
    qrCodeSize,
    qrCodeSize
  );

  // Section 3: Access Platform
  doc.setFontSize(14);
  doc.text(t("pdfLogin.howToAccess"), 15, section2Y + 80);

  // Web platform text
  doc.setFontSize(12);
  doc.text(t("pdfLogin.webPlatform"), 15, section2Y + 90);

  // Get the appropriate URL based on user type
  const platformUrls = {
    parent: "https://parent.edtrust.tn/#",
    student: "https://student.edtrust.tn/#",
    teacher: "https://teacher.edtrust.tn/#",
  };
  const platformUrl =
    platformUrls[userType?.toLowerCase()] || "https://parent.edtrust.tn/#";

  // Add the platform URL text
  doc.setFontSize(11);
  doc.text(platformUrl, 15, section2Y + 98);

  // Add app logo
  if (userType) {
    const userTypeLogos = {
      parent: "/resources/images/logos/EDparent.jpeg",
      student: "/resources/images/logos/EdStudent.jpeg",
      teacher: "/resources/images/logos/EdTeacher.jpeg",
    };
    const logoPath = userTypeLogos[userType.toLowerCase()];
    if (logoPath) {
      const appLogoImg = new Image();
      appLogoImg.src = logoPath;
      const logoSize = 23;
      const logoX = (pageWidth - logoSize) / 2;
      const logoY = section2Y + 103;

      // Add the image
      doc.addImage(appLogoImg, "JPEG", logoX, logoY, logoSize, logoSize);

      // Add clickable link over the logo
      doc.link(logoX, logoY, logoSize, logoSize, {
        url: platformUrl,
      });
    }
  }

  // Application mobile text and URL
  doc.setFontSize(12);
  doc.text(t("pdfLogin.mobileApp"), 15, section2Y + 135);
  doc.setFontSize(11);

  // Get the appropriate app name based on user type
  const appNames = {
    parent: "EdParent",
    student: "EdStudent",
    teacher: "EdTeacher",
  };
  const appName = appNames[userType?.toLowerCase()] || t("pdfLogin.edParent");

  doc.text(t("pdfLogin.installApp", { appName }), 15, section2Y + 143);

  // Add app store images centered in a row
  const appStoreImages = [
    "/resources/images/media/appStore.jpeg",
    "/resources/images/media/googlePlay.jpeg",
    "/resources/images/media/huawei.jpeg",
  ];

  const imageWidth = 45;
  const imageHeight = 20;
  const imageSpacing = 5;
  const totalWidth =
    appStoreImages.length * imageWidth +
    (appStoreImages.length - 1) * imageSpacing;
  const startX = (pageWidth - totalWidth) / 2;
  const imageY = section2Y + 150;

  const appStoreUrls = getAppStoreUrls(userType);

  appStoreImages.forEach((imagePath, index) => {
    const appStoreImg = new Image();
    appStoreImg.src = imagePath;
    const imageX = startX + index * (imageWidth + imageSpacing);

    // Add the image
    doc.addImage(appStoreImg, "JPEG", imageX, imageY, imageWidth, imageHeight);

    // Add clickable link over the image
    doc.link(imageX, imageY, imageWidth, imageHeight, {
      url: appStoreUrls[index],
    });
  });

  // Add important information section
  doc.setFontSize(12);
  doc.text(t("pdfLogin.importantInfo"), 15, imageY + 30);

  doc.setFontSize(10);
  doc.text(t("pdfLogin.changePassword"), 15, imageY + 40);
  doc.text(t("pdfLogin.keepDocumentSafe"), 15, imageY + 48);
  doc.text(t("pdfLogin.contactAdmin"), 15, imageY + 56);

  // Save the PDF
  const finalFilename = `${userName}.pdf`;
  doc.save(finalFilename);

  return doc;
};

// Define URLs for each app store based on user type
const getAppStoreUrls = (userType) => {
  const urls = {
    parent: [
      "https://apps.apple.com/us/app/edparent/id6469570430", // App Store URL
      "https://play.google.com/store/apps/details?id=com.edcontrolpaternal.controlpaternal", // Google Play URL
      "https://appgallery.huawei.com/app/C107441097", // Huawei AppGallery URL
    ],
    teacher: [
      "https://apps.apple.com/us/app/edteacher/id6471353124", // App Store URL
      "https://play.google.com/store/apps/details?id=com.edtrust.edteacher", // Google Play URL
      "https://appgallery.huawei.com/app/C109015147", // Huawei AppGallery URL
    ],
    student: [
      "https://apps.apple.com/tn/app/edstudent/id6751495124", // App Store URL
      "https://play.google.com/store/apps/details?id=com.edtrust.edstudent", // Google Play URL
      "https://appgallery.huawei.com/app/C115080693", // Huawei AppGallery URL
    ],
  };

  // Default to parent URLs if userType is not specified or not found
  return urls[userType?.toLowerCase()] || urls.parent;
};

export default {
  generateLoginPdf,
};
